import React from 'react';
import { createPortal } from 'react-dom';
import { X, Check } from 'react-feather';
import classnames from 'classnames';
import styles from './styles/PaymentSplashScreen.module.scss';
import { Button, IButtonProps } from 'components/quashed';
import { LoadingDots } from 'components/loading';
import useTracking from 'hooks/useTracking';

interface IProps {
  buttonProps?: IButtonProps;
  error?: boolean;
  eventName?: string;
  loading?: boolean;
  note?: React.ReactNode;
  q4eLogoSrc?: string;
  quashedLogo?: React.ComponentType<{ className: string }>;
  subtitle?: string;
  title: string;
  preSubmitCheck?: React.ReactNode;
}

const PaymentSplashScreen: React.FC<IProps> = ({
  buttonProps,
  error = false,
  eventName,
  loading = false,
  note,
  subtitle,
  title,
  q4eLogoSrc,
  quashedLogo: CustomQuashedLogo,
  preSubmitCheck,
}) => {
  useTracking(eventName);

  // Guard clause to avoid creating the portal if the target element is not found
  const modalRoot = document.getElementById('app');
  if (!modalRoot) {
    return null;
  }

  return createPortal(
    <div className={classnames(styles.container, { [styles.error]: error })}>
      <div className={styles.curveContainer}>
        <svg
          className={styles.curve}
          xmlns="http://www.w3.org/2000/svg"
          width="100%"
          height="100%"
          viewBox="0 0 1440 309"
          fill="none"
        >
          <path
            d="M-25 -698H1468V190.629C1468 190.629 1015.2 309.468 717.672 308.999C423.09 308.534 -25 190.629 -25 190.629V-698Z"
            fill={error ? '#E47C6E' : '#54A702'}
            fillOpacity="0.1"
          />
        </svg>
      </div>
      <div className={styles.body}>
        <div className={styles.checkContainer}>
          <div className={styles.check}>
            {error ? (
              <X size={30} strokeLinecap="square" strokeWidth={3} />
            ) : (
              <Check size={30} strokeLinecap="square" strokeWidth={3} />
            )}
          </div>
        </div>

        <div className={styles.titleContainer}>
          <h1 className={styles.title}>{title}</h1>
          {subtitle && <h4 className={styles.subtitle}>{subtitle}</h4>}
          {note}
        </div>

        {preSubmitCheck}

        <div className={styles.footer}>
          <div className={styles.loader}>
            {loading ? (
              <LoadingDots />
            ) : (
              <Button
                {...buttonProps}
                className={classnames(styles.button, buttonProps?.className)}
              />
            )}
          </div>
          <div className={styles.logoContainer}>
            {CustomQuashedLogo && <CustomQuashedLogo className={styles.logo} />}
            {q4eLogoSrc && (
              <>
                <div className={styles.vl} />
                <img
                  src={q4eLogoSrc}
                  alt="Q4E Logo"
                  className={styles.q4eLogo}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    modalRoot,
  );
};

export default PaymentSplashScreen;
